import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { siteConfig } from '@/config/site';
import {
    ArrowRight,
    Lightbulb,
    TrendingUp,
    Globe,
    Heart,
    Briefcase,
} from 'lucide-react';

const perks = [
    {
        icon: Lightbulb,
        title: 'Innovation',
        description:
            'Work on cutting-edge trading technology and help shape the future of proprietary trading.',
    },
    {
        icon: TrendingUp,
        title: 'Growth',
        description:
            'Accelerate your career with mentorship, training programs, and clear advancement paths.',
    },
    {
        icon: Globe,
        title: 'Global Team',
        description:
            'Join a diverse, remote-first team of traders, engineers, and business professionals from around the world.',
    },
    {
        icon: Heart,
        title: 'Competitive Benefits',
        description:
            'Enjoy competitive compensation, performance bonuses, and comprehensive benefits packages.',
    },
];

export default function Careers() {
    return (
        <AppLayout title={`Careers - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <div className="text-center max-w-4xl mx-auto">
                    <FadeIn>
                        <Badge className="mb-6">Careers</Badge>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight">
                            Join Our <span className="text-gradient">Team</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.2}>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                            Be part of a fast-growing fintech company that is redefining proprietary
                            trading. We are looking for passionate individuals who want to make a real
                            impact.
                        </p>
                    </FadeIn>
                </div>
            </Section>

            {/* Why Work Here */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Why Work at"
                        highlight={`${siteConfig.brand.name}`}
                        description="We invest in our people because they are the foundation of everything we build."
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid sm:grid-cols-2 gap-6">
                        {perks.map((perk) => {
                            const Icon = perk.icon;
                            return (
                                <motion.div key={perk.title} variants={staggerItem}>
                                    <Card className="rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors duration-300 p-0 h-full">
                                        <CardContent className="p-6 pt-6">
                                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 border border-primary/10 mb-4">
                                                <Icon className="h-6 w-6 text-primary" />
                                            </div>
                                            <h3 className="text-lg font-semibold text-foreground mb-2">
                                                {perk.title}
                                            </h3>
                                            <p className="text-sm text-muted-foreground leading-relaxed">
                                                {perk.description}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </StaggerChildren>
            </Section>

            {/* Open Positions */}
            <Section className="bg-background">
                <FadeIn>
                    <SectionHeader
                        title="Open"
                        highlight="Positions"
                        description="We are always looking for exceptional talent to join our growing team."
                    />
                </FadeIn>
                <FadeIn delay={0.1}>
                    <div className="max-w-3xl mx-auto">
                        <Card className="rounded-xl border border-border bg-card/80 backdrop-blur-sm p-0">
                            <CardContent className="p-8 pt-8 text-center">
                                <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary/10 border border-primary/10 mx-auto mb-5">
                                    <Briefcase className="h-7 w-7 text-primary" />
                                </div>
                                <h3 className="text-xl font-bold text-foreground mb-3">
                                    No Open Positions Right Now
                                </h3>
                                <p className="text-muted-foreground leading-relaxed max-w-lg mx-auto">
                                    We don't have any open positions at the moment, but we're always
                                    interested in hearing from talented individuals. Send us your resume
                                    and we`ll keep you in mind for future opportunities.
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </FadeIn>
            </Section>

            {/* CTA */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
                            Interested in <span className="text-gradient">Joining?</span>
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                            Send us your resume and tell us how you can contribute to the {siteConfig.brand.name}
                            mission. We`d love to hear from you.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link to="/contact">
                                <Button size="lg">
                                    Contact Us
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
